/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, Badge, Card, CardBody, FormGroup, Input, Label, Spinner } from "reactstrap";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { getPermission } from "utils/Utils";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PaginationComponent,
  RSelect,
} from "../../../components/Component";
import { getDepartments } from "../../../services/departments";
import { getChannelIdByDepartementId } from "../../../services/channel";
import { serviceGetUsersSalesAndCsByDeparmentId } from "../../../services/users";
import "../../../assets/css/chatImportWaBackup.css";
import {
  FILTERS,
  ImportProgressBanner,
  ChatItemsTable,
  ImportResult,
  ImportStepper,
  ReviewSummary,
  UploadDropzone,
  isMappable,
  isOk,
  matchesSearch,
  needsMapping,
} from "./ChatImportWaBackupParts";
import {
  confirmWaBackupImport,
  getWaBackupStatus,
  resolveWaBackupMatch,
  uploadWaBackup,
} from "../../../services/chatImport";

const POLL_INTERVAL_MS = 2000;
// Mirrors the backend's multer limits (chat-import.controller.ts) so the
// admin hears about a too-big batch before uploading it.
const MAX_FILES = 20;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
// A status poll can fail on a network blip — only give up after this many
// failures in a row.
const MAX_POLL_FAILURES = 3;
// Backoff ceiling while reconnecting during an import.
const MAX_POLL_BACKOFF_MS = 15000;
// No progress for this long during an import = the server-side run likely
// died (e.g. backend restart) — offer to resume it.
const STALL_AFTER_MS = 3 * 60 * 1000;
const ACCEPTED_EXTENSIONS = [".txt", ".zip"];

const PAGE_SIZE = 25;

const toOptions = (items, valueKey, labelKey) =>
  items.map((item) => ({ value: item[valueKey], label: item[labelKey] }));

const hasAcceptedExtension = (name) => ACCEPTED_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

const ChatImportWaBackup = () => {
  const [cookies] = useCookies(["SIDikey", "departmentIds", "id"]);
  const token = cookies.SIDikey;
  // Same source Chat.js uses for its own department filter — roleFunctions
  // isn't a cookie, it's the per-page access list cached in localStorage
  // after login.
  const roleFunctions = getPermission()["chats"]?.access || [];

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);

  const [department, setDepartment] = useState(null);
  const [channel, setChannel] = useState(null);
  const [sales, setSales] = useState(null);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // PENDING | PROCESSING | DONE | FAILED | IMPORTING
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null); // { processed, total } while importing
  const [connectionRetries, setConnectionRetries] = useState(0);
  const [isStalled, setIsStalled] = useState(false);
  const [isRestored, setIsRestored] = useState(false);
  const lastProgress = useRef({ processed: -1, at: 0 });
  // Department/channel of a job restored from ?job=, applied once their
  // dropdown options have loaded.
  const restoreTarget = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const pollTimer = useRef(null);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedKey, setExpandedKey] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [rowErrors, setRowErrors] = useState({});
  const [resolveError, setResolveError] = useState("");

  const [filterKey, setFilterKey] = useState(null); // null = first filter that has items
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const [shouldSkipUnknown, setShouldSkipUnknown] = useState(true);
  // The per-chat table is opt-in: most imports only need the summary.
  const [showDetails, setShowDetails] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    (async () => {
      const response = await getDepartments(token, "", cookies.departmentIds, cookies.id, roleFunctions);
      if (response?.data?.data) {
        setDepartmentOptions(toOptions(response.data.data, "id", "name"));
      }
    })();
  }, []);

  useEffect(() => {
    if (!department) {
      setChannelOptions([]);
      setSalesOptions([]);
      setChannel(null);
      setSales(null);
      return;
    }

    (async () => {
      const channelResponse = await getChannelIdByDepartementId(token, department.value);
      if (channelResponse?.data?.data) {
        setChannelOptions(
          channelResponse.data.data.map((item) => ({
            value: item.id,
            label: item.phoneNumber ? `${item.name} (${item.phoneNumber})` : item.name,
          })),
        );
      }

      const salesUsers = await serviceGetUsersSalesAndCsByDeparmentId(token, department.value);
      if (Array.isArray(salesUsers)) {
        setSalesOptions(salesUsers.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` })));
      }
    })();
  }, [department]);

  useEffect(() => {
    const departmentId = restoreTarget.current?.departmentId;
    if (!departmentId || department) return;
    const option = departmentOptions.find((item) => item.value === departmentId);
    if (option) setDepartment(option);
  }, [departmentOptions]);

  useEffect(() => {
    const channelId = restoreTarget.current?.channelId;
    if (!channelId) return;
    const option = channelOptions.find((item) => item.value === channelId);
    if (option) {
      setChannel(option);
      restoreTarget.current = null;
    }
  }, [channelOptions]);

  // Stop polling on unmount so a leftover timer doesn't keep firing (and
  // calling setState) after the user navigates away.
  useEffect(() => () => clearTimeout(pollTimer.current), []);

  // The jobId lives in the URL (?job=...) so a reload or a reopened link
  // picks the batch back up — table, running import progress or report —
  // instead of losing it. Staging lives server-side for 24h.
  useEffect(() => {
    const restoredJobId = new URLSearchParams(location.search).get("job");
    if (!restoredJobId) return;

    (async () => {
      const response = await getWaBackupStatus(token, restoredJobId);

      if (!isOk(response)) {
        history.replace({ search: "" });
        setUploadError(response?.data?.message || "Import sebelumnya tidak ditemukan atau sudah kedaluwarsa");
        return;
      }

      const status = response.data;
      setIsRestored(true);
      restoreTarget.current = { departmentId: status.departmentId, channelId: status.channelId };
      setJobId(restoredJobId);
      setJobStatus(status.status);
      setItems(status.items || []);
      setReport(status.report || null);
      setProgress(status.progress || null);

      if (status.status === "PENDING" || status.status === "PROCESSING") {
        startUploadPolling(restoredJobId);
      } else if (status.status === "IMPORTING") {
        startImportPolling(restoredJobId);
      } else if (status.status === "FAILED") {
        setUploadError(status.error || "Proses parsing gagal");
      } else if (status.error) {
        setConfirmError(status.error);
      }
    })();
  }, []);

  const resetForNewUpload = () => {
    clearTimeout(pollTimer.current);
    history.replace({ search: "" });
    restoreTarget.current = null;
    setChannel(null);
    setIsStalled(false);
    setIsRestored(false);
    setProgress(null);
    setConnectionRetries(0);
    setFiles([]);
    setUploadError("");
    setJobId(null);
    setJobStatus(null);
    setItems([]);
    setReport(null);
    setSelectedKeys([]);
    setExpandedKey(null);
    setRowErrors({});
    setResolveError("");
    setConfirmError("");
    setFilterKey(null);
    setSearchQuery("");
    setPage(1);
    setShowDetails(false);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const rejected = [];
    const accepted = incoming.filter((file) => {
      if (!hasAcceptedExtension(file.name)) {
        rejected.push(`${file.name}: format harus .txt atau .zip`);
        return false;
      }
      if (file.size > MAX_FILE_BYTES) {
        rejected.push(`${file.name}: lebih dari 20MB`);
        return false;
      }
      return true;
    });

    const merged = [...files];
    accepted.forEach((file) => {
      if (!merged.some((existing) => existing.name === file.name && existing.size === file.size)) {
        merged.push(file);
      }
    });

    if (merged.length > MAX_FILES) {
      rejected.push(`Maksimal ${MAX_FILES} file per upload`);
    }

    setFiles(merged.slice(0, MAX_FILES));
    setUploadError(rejected.join("; "));
  };

  const removeFile = (index) => setFiles(files.filter((_, fileIndex) => fileIndex !== index));

  // Polls until `isFinished(status)`, then hands the final status payload to
  // `onFinished`; `onTick` sees every successful poll. With `keepTrying` (a
  // running import) a network failure never ends polling — the import keeps
  // running server-side, so the page backs off and reconnects instead.
  const pollStatus = (currentJobId, options, failures = 0) => {
    const { isFinished, onFinished, onTick, keepTrying = false } = options;
    const delay = failures === 0 ? POLL_INTERVAL_MS : Math.min(POLL_INTERVAL_MS * 2 ** failures, MAX_POLL_BACKOFF_MS);

    pollTimer.current = setTimeout(async () => {
      const response = await getWaBackupStatus(token, currentJobId);

      if (!isOk(response)) {
        // A 4xx (job expired, not yours) won't fix itself by retrying.
        const isPermanent = response.status >= 400 && response.status < 500;

        if (!isPermanent && (keepTrying || failures + 1 < MAX_POLL_FAILURES)) {
          setConnectionRetries(failures + 1);
          pollStatus(currentJobId, options, failures + 1);
          return;
        }
        setConnectionRetries(0);
        setJobStatus("FAILED");
        setUploadError(response?.data?.message || "Gagal memeriksa status import");
        return;
      }

      setConnectionRetries(0);
      setJobStatus(response.data.status);
      if (onTick) onTick(response.data);

      if (isFinished(response.data)) {
        onFinished(response.data);
      } else {
        pollStatus(currentJobId, options);
      }
    }, delay);
  };

  const startUploadPolling = (currentJobId) =>
    pollStatus(currentJobId, {
      isFinished: (status) => status.status !== "PENDING" && status.status !== "PROCESSING",
      onFinished: (status) => {
        if (status.status === "FAILED") {
          setUploadError(status.error || "Proses parsing gagal");
          return;
        }
        setItems(status.items || []);
      },
    });

  const startImportPolling = (currentJobId) => {
    lastProgress.current = { processed: -1, at: Date.now() };
    setIsStalled(false);
    pollStatus(currentJobId, {
      keepTrying: true,
      isFinished: (status) => status.status !== "IMPORTING",
      onTick: (status) => {
        const processed = status.progress ? status.progress.processed : -1;
        if (processed !== lastProgress.current.processed) {
          lastProgress.current = { processed, at: Date.now() };
        }
        setIsStalled(Date.now() - lastProgress.current.at > STALL_AFTER_MS);
        setProgress(status.progress || null);
        if (status.items) setItems(status.items);
      },
      onFinished: (status) => {
        setIsStalled(false);
        setReport(status.report || null);
        if (status.error) setConfirmError(status.error);
      },
    });
  };

  const handleUpload = async () => {
    if (!channel || files.length === 0) {
      setUploadError("Pilih channel dan file backup terlebih dahulu");
      return;
    }

    setUploading(true);
    setUploadError("");

    const response = await uploadWaBackup(token, channel.value, files);

    setUploading(false);

    if (!isOk(response)) {
      setUploadError(response?.data?.message || "Gagal mengunggah file backup");
      return;
    }

    setJobId(response.data.jobId);
    setJobStatus("PENDING");
    history.replace({ search: `?job=${response.data.jobId}` });
    startUploadPolling(response.data.jobId);
  };

  const handleResolve = async (entries) => {
    if (entries.length === 0) return;

    setResolving(true);
    setResolveError("");

    const response = await resolveWaBackupMatch(token, jobId, { items: entries });

    setResolving(false);

    if (!isOk(response)) {
      setResolveError(response?.data?.message || "Gagal menyimpan pilihan kontak");
      return;
    }

    const nextErrors = { ...rowErrors };
    response.data.results.forEach((result) => {
      if (result.ok) {
        delete nextErrors[result.itemKey];
      } else {
        nextErrors[result.itemKey] = result.error;
      }
    });

    setRowErrors(nextErrors);
    setItems(response.data.items || items);
    setSelectedKeys([]);
    setExpandedKey(null);
  };

  const selectedItems = items.filter((item) => selectedKeys.includes(item.key));

  const handleBulkCreate = () => {
    const entries = selectedItems
      .filter((item) => needsMapping(item) && item.match.kind === "UNMATCHED_WITH_PHONE")
      .map((item) => ({ itemKey: item.key, action: "CREATE_CONTACT" }));

    if (entries.length === 0) {
      setResolveError("Tidak ada chat terpilih yang punya nomor dan belum punya kontak");
      return;
    }
    handleResolve(entries);
  };

  const handleBulkIgnore = () =>
    handleResolve(selectedItems.filter(isMappable).map((item) => ({ itemKey: item.key, action: "IGNORE" })));

  const handleConfirm = async () => {
    if (!department || !sales) {
      setConfirmError("Pilih department dan sales/CS terlebih dahulu");
      return;
    }

    clearTimeout(pollTimer.current);
    setConfirmError("");
    setReport(null);

    const response = await confirmWaBackupImport(token, jobId, {
      departmentId: department.value,
      salesId: sales.value,
      skipUnknownContacts: shouldSkipUnknown,
    });

    if (!isOk(response)) {
      setConfirmError(response?.data?.message || "Gagal mengonfirmasi import");
      // A failed resume of a stalled import: keep watching the old run.
      if (jobStatus === "IMPORTING") startImportPolling(jobId);
      return;
    }

    setJobStatus("IMPORTING");
    setProgress(null);
    startImportPolling(jobId);
  };

  const toggleSelected = (key) =>
    setSelectedKeys(selectedKeys.includes(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]);

  const filterCounts = Object.fromEntries(FILTERS.map((filter) => [filter.key, items.filter(filter.matches).length]));
  const activeFilter =
    FILTERS.find((filter) => filter.key === filterKey) ||
    FILTERS.find((filter) => filter.key !== "ALL" && filterCounts[filter.key] > 0) ||
    FILTERS[FILTERS.length - 1];
  const visibleItems = items.filter((item) => activeFilter.matches(item) && matchesSearch(item, searchQuery));
  const pageCount = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visibleItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // "Select all" covers the whole filtered result, not just the page shown.
  const selectableKeys = visibleItems.filter(isMappable).map((item) => item.key);
  const isAllSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key));
  const unmappedCount = items.filter(needsMapping).length;
  const createCount = selectedItems.filter(
    (item) => needsMapping(item) && item.match.kind === "UNMATCHED_WITH_PHONE",
  ).length;
  const ignoreCount = selectedItems.filter(isMappable).length;

  // Selection is per filter view — switching view starts a fresh selection
  // so a bulk action never hits rows the admin can't see.
  const changeView = (nextFilterKey, nextQuery) => {
    setFilterKey(nextFilterKey);
    setSearchQuery(nextQuery);
    setPage(1);
    setSelectedKeys([]);
    setExpandedKey(null);
  };

  // Summary numbers. "Ready" mirrors the backend's decideConfirm IMPORT case.
  const readyCount = items.filter(
    (item) => isMappable(item) && !item.ignored && (item.resolvedContactId || item.match.kind === "MATCHED"),
  ).length;
  const unmatchedCount = items.filter((item) => isMappable(item) && (item.ignored || needsMapping(item))).length;
  const invalidCount = items.filter((item) => item.error).length;

  const isProcessing = jobId && (jobStatus === "PENDING" || jobStatus === "PROCESSING");
  const isImporting = jobStatus === "IMPORTING";
  const isReviewing = jobStatus === "DONE";
  const isBusy = resolving || isImporting;
  // A stalled import unlocks Department/Sales so "Lanjutkan import" can be
  // sent (they're empty after a page reload).
  const isFormLocked = isImporting && !isStalled;
  const currentStep = !jobId ? 0 : isImporting ? 2 : report ? 3 : 1;

  return (
    <React.Fragment>
      <Head title="WhatsApp Recovery"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                WhatsApp Recovery
              </BlockTitle>
              <p>Pulihkan riwayat chat WhatsApp dari file backup ke CRM.</p>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <CardBody>
              <ImportStepper current={currentStep} />

              <FormGroup>
                <Label>Department</Label>
                <RSelect
                  options={departmentOptions}
                  value={department}
                  onChange={setDepartment}
                  isDisabled={isFormLocked}
                />
              </FormGroup>

              <FormGroup>
                <Label>Channel / Nomor WA</Label>
                <RSelect
                  options={channelOptions}
                  value={channel}
                  onChange={setChannel}
                  isDisabled={!department || Boolean(jobId)}
                  placeholder={department ? "Pilih channel" : "Pilih department dahulu"}
                />
              </FormGroup>

              <FormGroup>
                <Label>Assign ke (Sales/CS)</Label>
                <RSelect
                  options={salesOptions}
                  value={sales}
                  onChange={setSales}
                  isDisabled={!department || isFormLocked}
                  placeholder={department ? "Pilih sales/CS" : "Pilih department dahulu"}
                />
              </FormGroup>

              {!jobId && <UploadDropzone files={files} onAdd={addFiles} onRemove={removeFile} maxFiles={MAX_FILES} />}

              {uploadError && <Alert color="danger">{uploadError}</Alert>}

              {!jobId && (
                <Button color="primary" onClick={handleUpload} disabled={uploading || !channel || files.length === 0}>
                  {uploading ? <Spinner size="sm" /> : <Icon name="upload-cloud" />}
                  <span> Proses {files.length > 0 ? `${files.length} File` : "File"}</span>
                </Button>
              )}

              {jobId && !isProcessing && !isReviewing && !isImporting && (
                <Button color="light" className="mt-2" onClick={resetForNewUpload}>
                  Mulai import baru
                </Button>
              )}

              {isProcessing && (
                <Alert color="info" className="mt-3">
                  <Spinner size="sm" className="me-2" />
                  Sedang memproses file backup ({jobStatus === "PENDING" ? "menunggu" : "parsing"})... Jangan tutup
                  halaman ini.
                </Alert>
              )}

              {isRestored && !isImporting && jobStatus === "DONE" && !report && !sales && (
                <Alert color="info" className="mt-3">
                  Halaman dipulihkan dari import sebelumnya. Pilih Sales/CS lagi sebelum konfirmasi.
                </Alert>
              )}

              {isImporting && (
                <ImportProgressBanner
                  progress={progress}
                  isStalled={isStalled}
                  connectionRetries={connectionRetries}
                  canResume={Boolean(department && sales)}
                  resumeError={confirmError}
                  onResume={handleConfirm}
                />
              )}

              {(isReviewing || isImporting) && (
                <div className={`mt-4${isImporting ? " wa-locked" : ""}`} aria-busy={isImporting}>
                  <div className="d-flex justify-content-end mb-2">
                    <Button color="light" size="sm" onClick={resetForNewUpload} disabled={isImporting}>
                      <Icon name="reload" />
                      <span>Mulai import baru</span>
                    </Button>
                  </div>

                  {report ? (
                    <ImportResult report={report} totalChats={items.length} />
                  ) : (
                    <ReviewSummary
                      total={items.length}
                      ready={readyCount}
                      unmatched={unmatchedCount}
                      invalid={invalidCount}
                      shouldSkipUnknown={shouldSkipUnknown}
                    />
                  )}

                  {confirmError && <Alert color="danger">{confirmError}</Alert>}

                  {(!report || readyCount > 0) && (
                    <Button color="success" size="lg" onClick={handleConfirm} disabled={isBusy || readyCount === 0}>
                      {isImporting ? <Spinner size="sm" /> : <Icon name="check-circle-cut" />}
                      <span>
                        {" "}
                        {report
                          ? `Import ulang ${readyCount.toLocaleString("id-ID")} chat yang belum masuk`
                          : `Import ${readyCount.toLocaleString("id-ID")} chat`}
                      </span>
                    </Button>
                  )}

                  <div className="mt-3">
                    <Button color="link" className="px-0" onClick={() => setShowDetails(!showDetails)}>
                      <Icon name={showDetails ? "chevron-down" : "chevron-right"} />
                      <span>
                        {showDetails ? "Sembunyikan detail per chat" : "Lihat & atur detail per chat (opsional)"}
                      </span>
                    </Button>
                  </div>

                  {showDetails && (
                    <div className="wa-details mt-2">
                      <div className="wa-filter-chips mb-2">
                        {FILTERS.filter((filter) => filter.key === "ALL" || filterCounts[filter.key] > 0).map(
                          (filter) => (
                            <Button
                              key={filter.key}
                              size="sm"
                              color={filter.key === activeFilter.key ? "primary" : "light"}
                              disabled={isBusy}
                              onClick={() => changeView(filter.key, searchQuery)}
                            >
                              {filter.label}{" "}
                              <Badge color="light" pill className="ms-1">
                                {filterCounts[filter.key]}
                              </Badge>
                            </Button>
                          ),
                        )}
                      </div>
                      <Input
                        type="search"
                        className="mb-2"
                        placeholder="Cari nama atau nomor..."
                        value={searchQuery}
                        onChange={(e) => changeView(activeFilter.key, e.target.value)}
                      />
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-soft">
                          {selectedKeys.length > 0
                            ? `${selectedKeys.length} dipilih`
                            : `${visibleItems.length} chat di tampilan ini`}
                        </span>
                        <div>
                          <Button
                            color="primary"
                            size="sm"
                            disabled={isBusy || createCount === 0}
                            onClick={handleBulkCreate}
                          >
                            Buat kontak ({createCount})
                          </Button>{" "}
                          <Button
                            color="light"
                            size="sm"
                            disabled={isBusy || ignoreCount === 0}
                            onClick={handleBulkIgnore}
                          >
                            Abaikan ({ignoreCount})
                          </Button>
                        </div>
                      </div>
                      {resolveError && <Alert color="danger">{resolveError}</Alert>}
                      <ChatItemsTable
                        items={pageItems}
                        selectedKeys={selectedKeys}
                        selectableKeys={selectableKeys}
                        rowErrors={rowErrors}
                        expandedKey={expandedKey}
                        disabled={isBusy}
                        token={token}
                        onToggleAll={() => setSelectedKeys(isAllSelected ? [] : selectableKeys)}
                        onToggleSelected={toggleSelected}
                        onToggleExpanded={(key) => setExpandedKey(expandedKey === key ? null : key)}
                        onResolve={handleResolve}
                      />
                      {visibleItems.length > PAGE_SIZE && (
                        <div className="mb-3">
                          <PaginationComponent
                            itemPerPage={PAGE_SIZE}
                            totalItems={visibleItems.length}
                            paginate={(nextPage) => {
                              setPage(nextPage);
                              setExpandedKey(null);
                            }}
                            currentPage={currentPage}
                          />
                        </div>
                      )}
                      <div className="custom-control custom-checkbox mb-3" style={{ display: "block" }}>
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="skipUnknownContacts"
                          checked={shouldSkipUnknown}
                          disabled={isBusy}
                          onChange={(e) => setShouldSkipUnknown(e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="skipUnknownContacts">
                          Lewati otomatis chat yang belum punya kontak di CRM
                        </label>
                      </div>
                      {!shouldSkipUnknown && unmappedCount > 0 && (
                        <Alert color="warning">
                          {unmappedCount} chat belum punya kontak dan akan tercatat gagal. Pilih kontaknya dulu atau
                          aktifkan opsi lewati otomatis.
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ChatImportWaBackup;
